import { Component, Input, OnChanges, SimpleChanges, Output, EventEmitter, OnDestroy, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CustomFieldModel } from '@base/modules/rest/custom-field/model/custom-field.model';
import { CustomFieldTypesEnum } from '@base/modules/rest/custom-field/enums/custom-field-types.enum';
import { ObjectCompositeFilterModel } from '@base/modules/rest/object/model/object-composite-filter.model';
import { ObjectCompositeFilterItemModel } from '@shared/components/filter/model/object-composite-filter-item.model';
import { FilterOperationEnum } from '@shared/components/filter/enums/filter-operation.enum';
import { ExpressionOperatorEnum } from '@base/modules/rest/object/enums/expression-operator.enum';
import { createFieldFilterItem, generateCompositeFilterExpression } from '@views/secured/object/utils/composite-filters.util';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { findFieldForFilter } from '@shared/components/filter/pipes/applied-filters-chips-display.pipe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export enum ActiveFiltersHeightTypeEnum {
  EXPANDED = 'expanded',
  COLLAPSED = 'collapsed',
}

const FIELDS_TYPES = [
  CustomFieldTypesEnum.TEXT,
  CustomFieldTypesEnum.TEXTAREA,
  CustomFieldTypesEnum.EMAIL,
  CustomFieldTypesEnum.URL,
  CustomFieldTypesEnum.NUMBER,
  CustomFieldTypesEnum.DECIMAL_NUMBER,
];

interface ActiveFilterFormModel {
  fieldName: FormControl<string>;
  operation: FormControl<FilterOperationEnum>;
  value: FormControl<any>;
}

interface ActiveFiltersFormModel {
  activeFilters: FormArray<FormGroup<ActiveFilterFormModel>>;
}
@UntilDestroy()
@Component({
  selector: 'app-active-filters',
  templateUrl: './active-filters.component.html',
  styleUrls: ['./active-filters.component.scss'],
})
export class ActiveFiltersComponent implements OnChanges, OnDestroy {
  @Input() fields: CustomFieldModel[];
  @Input() compositeFilter: ObjectCompositeFilterModel;
  @Input() isReport: boolean;
  @Input() activeFiltersHeight: ActiveFiltersHeightTypeEnum = ActiveFiltersHeightTypeEnum.COLLAPSED;
  @Output() applyFiltersEvent = new EventEmitter<ObjectCompositeFilterModel>();
  @Output() isExpandedEvent = new EventEmitter<ActiveFiltersHeightTypeEnum>();

  form: FormGroup<ActiveFiltersFormModel>;
  fieldsMap: Map<string, CustomFieldModel> = new Map();
  private formSubscription: Subscription;

  constructor(private translate: TranslateService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fields || changes.compositeFilter) {
      if (!this.isReport) {
        this.generateForm();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  private generateForm(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }

    this.form = new FormGroup<ActiveFiltersFormModel>({
      activeFilters: new FormArray<FormGroup<ActiveFilterFormModel>>([]),
    });

    if (!this.compositeFilter?.filters?.length || !this.fields?.length) {
      return;
    }

    this.fieldsMap.clear();
    this.fields.forEach(field => {
      this.fieldsMap.set(field.name, field);
    });

    this.compositeFilter.filters
      .forEach(filter => {
        this.addActiveFilterFormGroup(filter);
      });

    //this.setupFormSubscription();
    this.setFieldsValueChanges();
  }

  setFieldsValueChanges(): void {
    this.form.controls.activeFilters.controls
      .filter(fg => {
        const field = this.getField(fg.value.fieldName);
        return field && !this.isStringFieldType(field)
      })
      .forEach(fg => {
        fg.controls.value.valueChanges
          .pipe(
            untilDestroyed(this),
            debounceTime(300)
          ).subscribe(value => {
            this.emitFiltersChange();
          });
      });
  }

  private setupFormSubscription(): void {
    if (this.form) {
      this.formSubscription = this.form.valueChanges
        .pipe(debounceTime(300))
        .subscribe(() => {
          this.emitFiltersChange();
        });
    }
  }

  onKeyupEnter(field: CustomFieldModel): void {
    const isStringFieldType = this.isStringFieldType(field);
    if (isStringFieldType) {
      this.emitFiltersChange();
    }
  }

  private isStringFieldType(field: CustomFieldModel): boolean {
    return FIELDS_TYPES.includes(field.type);
  }


  private emitFiltersChange(): void {
    const compositeFilter: ObjectCompositeFilterModel = {
      id: this.compositeFilter?.id,
      filters: [],
      expression: '',
      showChips: this.compositeFilter?.showChips,
      chosenFiltersSaved: this.compositeFilter?.chosenFiltersSaved,
    };

    this.activeFiltersGroups
      // .filter(fg => this.isFormGroupValid(fg))
      .forEach((fg, i) => {
        const field = this.getField(fg.value.fieldName);
        if (field) {
          const filterItem = createFieldFilterItem(`active_filter_${i}`, field, fg.value.value, fg.value.operation);
          compositeFilter.filters.push(filterItem);
        }
      });

    generateCompositeFilterExpression(compositeFilter, ExpressionOperatorEnum.AND);
    this.applyFiltersEvent.emit(compositeFilter);
  }

  isFormGroupValid(fg: FormGroup<ActiveFilterFormModel>): boolean {
    const value = fg?.value?.value;
    const operation = fg?.value?.operation;

    if (!operation) {
      return false;
    }

    const noValueOperations = [FilterOperationEnum.HAS_VALUE, FilterOperationEnum.DOES_NOT_HAVE_VALUE];
    if (noValueOperations.includes(operation)) {
      return true;
    }

    return value != null && value !== '';
  }

  private addActiveFilterFormGroup(filter: ObjectCompositeFilterItemModel): void {
    const field = findFieldForFilter(this.fields, filter);
    const fieldName = field?.name || filter.fieldName;

    if (field.type === CustomFieldTypesEnum.SWITCHER && typeof filter?.value === 'string') {
      filter.value = filter.value === 'true';
    }

    const formGroup = new FormGroup<ActiveFilterFormModel>({
      fieldName: new FormControl<string>(fieldName),
      operation: new FormControl<FilterOperationEnum>(filter.operation),
      value: new FormControl<any>(filter.value),
    });
    this.checkIfFormGroupReadonly(formGroup);
    this.activeFiltersArray.push(formGroup);
  }

  get activeFiltersArray(): FormArray<FormGroup<ActiveFilterFormModel>> {
    return this.form.get('activeFilters') as FormArray<FormGroup<ActiveFilterFormModel>>;
  }

  get activeFiltersGroups(): FormGroup<ActiveFilterFormModel>[] {
    return this.activeFiltersArray.controls;
  }

  getField(name: string): CustomFieldModel {
    if (this.fieldsMap.has(name)) {
      return this.fieldsMap.get(name);
    }
    const field = this.fields?.find(f => f.name === name);
    if (field) {
      this.fieldsMap.set(name, field);
    }
    return field;
  }

  createFieldLabel(filterFormGroup: FormGroup<ActiveFilterFormModel>): string {
    const fieldName = filterFormGroup.controls.fieldName.value;
    const operation = filterFormGroup.controls.operation.value;

    const field = this.getField(fieldName);
    const fieldLabel = field?.label || fieldName;
    const operatorText = this.translate.instant('FILTERS.OPERATORS.' + operation);

    return operation === FilterOperationEnum.EQUALS ? fieldLabel : `${fieldLabel} (${operatorText})`;
  }

  disableValueInput(operation: FilterOperationEnum): boolean {
    if (!operation) {
      return true;
    }
    return [FilterOperationEnum.HAS_VALUE, FilterOperationEnum.DOES_NOT_HAVE_VALUE].includes(operation);
  }

  toggleFiltersHeight(): void {
    if (this.activeFiltersHeight === ActiveFiltersHeightTypeEnum.COLLAPSED) {
      this.activeFiltersHeight = ActiveFiltersHeightTypeEnum.EXPANDED;
    } else {
      this.activeFiltersHeight = ActiveFiltersHeightTypeEnum.COLLAPSED;
    }
    if (!this.isReport && this.activeFiltersGroups?.length <= 6) {
      this.activeFiltersHeight = ActiveFiltersHeightTypeEnum.COLLAPSED;
    }
    this.isExpandedEvent.emit(this.activeFiltersHeight);
  }

  checkIfFormGroupReadonly(group: FormGroup<ActiveFilterFormModel>): void {
    const field = this.getField(group.controls.fieldName.value);
    field?.readOnly ? group.disable() : group.enable();
  }

  protected readonly ActiveFiltersHeightTypeEnum = ActiveFiltersHeightTypeEnum;
}
